'use client';
import { Locale, countryLanguages } from 'shared/src/types/languages.type';
import Share from '../../public/icons/share.svg';
import { useEffect } from 'react';
import Cookies from 'js-cookie';
import { publicCanonicalUrl } from '@/lib/runtime/app-origin';
import { getBridge } from '@/lib/native/bridge';

type ShareButtonProps = {
  lang: Locale;
  href: string;
  domain?: string;
  clientId?: string;
};

const ShareArticleButton = ({ lang, href, domain, clientId }: ShareButtonProps) => {
  const handleShare = async (): Promise<void> => {
    // Shared links must always point at the public canonical apex — never
    // window.location.origin, which inside the native app is the noindexed
    // app host. (Also fixes the old bug where a missing `domain` prop
    // produced a literal "undefined/en-CA/..." URL.)
    const fullUrl = domain
      ? `${domain}/${lang}${href}`
      : publicCanonicalUrl(`/${lang}${href}`, countryLanguages[lang]?.regionCode);

    // Prefer the native share sheet in the app shell — navigator.share is
    // unreliable inside Android WebViews.
    const bridge = getBridge();
    if (bridge) {
      try {
        await bridge.share({ title: document.title, url: fullUrl });
        return;
      } catch (error) {
        console.error('Native share failed, falling back:', error);
      }
    }

    // Then the Web Share API if available
    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title,
          url: fullUrl,
        });
        console.log('Article shared successfully');
      } catch (error) {
        console.error('Error sharing article:', error);
      }
    } else {
      // If Web Share API is not available, fallback to copying the link
      try {
        await navigator.clipboard.writeText(fullUrl);
      } catch (error) {
        console.error('Failed to copy link:', error);
      }
    }
  };

  useEffect(() => {
    if (clientId) {
      Cookies.set('clientId', clientId, { expires: 30 });
    }
  }, [clientId]);

  return (
    <button
      className={
        'fixed right-2 top-[4.3rem] z-30 flex items-center justify-center rounded-full border border-neutral-900/10 bg-light px-1.5 py-1 pt-2 dark:border-light dark:bg-dark xs:right-5'
      }
      onClick={handleShare}
      aria-label="Share Article"
      title="Share Article"
    >
      <Share className={'my-auto h-6 w-6 min-w-6 fill-slate-800 dark:fill-light'} />
    </button>
  );
};

export default ShareArticleButton;
